/**
 * E-GTCC-VIEW-DPLANE1 — enum → `.dot` tone.
 *
 * Every mapping below is a pure re-labelling of a status the API already
 * decided. Nothing is derived from a threshold and nothing is computed from
 * the local clock, so a source that reports "stale" can never be drawn as
 * healthy (Owner decision P8).
 */
import type { CockpitTone as FleetTone } from "../../../ui/primitives.js";
import type {
  BackupStatus,
  RestoreDrillStatus,
  TelemetryCleanupStatus,
  TenantPlacementStatus,
  VpnHealthStatus,
} from "../../../../data-plane/types.js";
import type {
  DbOpsDatabaseStatus,
  DbOpsHostLifecycleStatus,
  DbOpsPlanStatus,
  DbOpsReadinessStatus,
  DbOpsRiskLevel,
  PostgresInstanceStatus,
  TenantAssignmentStatus,
} from "../../../../db-ops/types.js";

const TONE_RANK: Readonly<Record<FleetTone, number>> = {
  ok: 0,
  info: 1,
  muted: 2,
  warn: 3,
  crit: 4,
};

/** The worst tone of a set — an empty set is "nothing measured", not "ok". */
export function worstTone(tones: ReadonlyArray<FleetTone>): FleetTone {
  if (tones.length === 0) return "muted";
  return tones.reduce((worst, tone) => (TONE_RANK[tone] > TONE_RANK[worst] ? tone : worst));
}

export function toneOfPlacement(status: TenantPlacementStatus): FleetTone {
  switch (status) {
    case "stable":
      return "ok";
    case "moving":
    case "split_target":
      return "warn";
    case "unknown":
      return "muted";
  }
}

export function toneOfBackup(status: BackupStatus): FleetTone {
  switch (status) {
    case "fresh":
      return "ok";
    case "stale":
      return "warn";
    case "missing":
    case "failing":
      return "crit";
    case "unknown":
      return "muted";
  }
}

export function toneOfRestoreDrill(status: RestoreDrillStatus): FleetTone {
  switch (status) {
    case "passed":
      return "ok";
    case "overdue":
    case "never_run":
      return "warn";
    case "failed":
      return "crit";
  }
}

export function toneOfCleanup(status: TelemetryCleanupStatus): FleetTone {
  switch (status) {
    case "ok":
      return "ok";
    case "behind":
      return "warn";
    case "failing":
      return "crit";
    case "unknown":
      return "muted";
  }
}

export function toneOfVpnHealth(status: VpnHealthStatus): FleetTone {
  switch (status) {
    case "healthy":
      return "ok";
    case "warning":
      return "warn";
    case "critical":
      return "crit";
    case "unknown":
      return "muted";
  }
}

export function toneOfReadiness(status: DbOpsReadinessStatus): FleetTone {
  switch (status) {
    case "ok":
      return "ok";
    case "warning":
      return "warn";
    case "critical":
      return "crit";
    case "unknown":
      return "muted";
  }
}

export function toneOfHostLifecycle(status: DbOpsHostLifecycleStatus): FleetTone {
  switch (status) {
    case "active":
      return "ok";
    case "planned":
    case "maintenance":
      return "warn";
    case "disabled":
    case "retired":
      return "muted";
    case "unknown":
      return "muted";
  }
}

export function toneOfInstance(status: PostgresInstanceStatus): FleetTone {
  switch (status) {
    case "online":
      return "ok";
    case "planned":
    case "maintenance":
    case "degraded":
      return "warn";
    case "offline":
      return "crit";
    case "unknown":
      return "muted";
  }
}

export function toneOfDatabase(status: DbOpsDatabaseStatus): FleetTone {
  switch (status) {
    case "available":
    case "assigned":
      return "ok";
    case "planned":
    case "maintenance":
    case "moving":
      return "warn";
    case "disabled":
      return "crit";
    case "archived":
    case "unknown":
      return "muted";
  }
}

export function toneOfAssignment(status: TenantAssignmentStatus): FleetTone {
  switch (status) {
    case "active":
      return "ok";
    case "planned":
    case "pending_move":
      return "warn";
    case "suspended":
      return "crit";
    case "archived":
    case "unknown":
      return "muted";
  }
}

export function toneOfPlanStatus(status: DbOpsPlanStatus): FleetTone {
  switch (status) {
    case "validated":
    case "approved":
    case "executed":
      return "ok";
    case "draft":
    case "validating":
    case "awaiting_approval":
      return "info";
    case "validation_failed":
    case "rejected":
    case "failed":
      return "crit";
    case "execution_disabled":
    case "cancelled":
    case "superseded":
      return "muted";
  }
}

export function toneOfRisk(level: DbOpsRiskLevel): FleetTone {
  switch (level) {
    case "low":
      return "ok";
    case "medium":
      return "info";
    case "high":
      return "warn";
    case "critical":
    case "forbidden":
      return "crit";
  }
}
